import type { CenterArea } from '../types/center.ts';
import type { GameState } from '../types/game-state.ts';
import { TURN_TIME_LIMIT_MS } from '../types/game-state.ts';
import type { LaneSlot, PlayerLane } from '../types/lane.ts';
import type { Move } from '../types/move.ts';
import type { Player } from '../types/player.ts';
import type { Holding, Tile } from '../types/tile.ts';
import { isBonusTrigger } from './bonus.ts';
import { laneIsFullyRevealed } from './lane.ts';
import { isPlaceable } from './placement.ts';
import { rotationCandidates } from './rotation.ts';

export type ApplyMoveResult = {
  state: GameState;
  bonusEarned: boolean;
};

export type ApplyMoveOptions = {
  /** 게임 종료(FINISHED 전이) 시 metrics.finishedAt에 기록할 시각. 기본 Date.now(). */
  now?: number;
};

/**
 * Move 1건을 적용. 검증은 호출 전 또는 내부에서 수행.
 * 검증 실패 시 throw. 성공 시 새 GameState 반환.
 */
export function applyMove(
  state: GameState,
  move: Move,
  options: ApplyMoveOptions = {},
): ApplyMoveResult {
  if (state.status !== 'IN_PROGRESS') {
    throw new Error(`cannot apply move: status is ${state.status}`);
  }

  switch (move.type) {
    case 'DRAW_FROM_DECK':
      return applyDrawFromDeck(state);
    case 'TAKE_FROM_POOL':
      return applyTakeFromPool(state, move.tileId);
    case 'PLACE':
      return applyPlace(state, move.resolvedNumber, move.targetIdx, options);
    case 'DISCARD_TO_POOL':
      return applyDiscardToPool(state, options);
    case 'SHUFFLE_DOWN_TILE':
      return applyShuffleDownTile(state, move.fromIdx, move.toIdx, options);
    default:
      throw new Error(`unknown move type: ${(move as { type: string }).type}`);
  }
}

function applyDrawFromDeck(state: GameState): ApplyMoveResult {
  if (state.holding) throw new Error('already holding a tile');
  if (state.center.deck.length === 0) throw new Error('deck is empty');

  const newDeck = state.center.deck.slice();
  const drawn = newDeck.pop();
  if (!drawn) throw new Error('unreachable');

  const heldTile: Holding = {
    ...drawn,
    face: 'UP',
    resolvedNumber: null, // 아직 회전 결정 안 함 (PLACE 시 결정)
    source: 'DECK',
  };

  return {
    state: {
      ...state,
      center: { deck: newDeck, openPool: state.center.openPool },
      holding: heldTile,
    },
    bonusEarned: false,
  };
}

function applyTakeFromPool(state: GameState, tileId: string): ApplyMoveResult {
  if (state.holding) throw new Error('already holding a tile');
  const idx = state.center.openPool.findIndex((t) => t.id === tileId);
  if (idx < 0) throw new Error(`tile ${tileId} not in pool`);

  const newPool = state.center.openPool.slice();
  const taken = newPool.splice(idx, 1)[0];
  if (!taken) throw new Error('unreachable');

  return {
    state: {
      ...state,
      center: { deck: state.center.deck, openPool: newPool },
      // 풀의 타일은 face=UP. resolvedNumber는 보존(이미 결정된 경우)되거나 null(DISCARD 출처).
      holding: { ...taken, source: 'POOL' },
    },
    bonusEarned: false,
  };
}

function applyPlace(
  state: GameState,
  resolvedNumber: number,
  targetIdx: number,
  options: ApplyMoveOptions = {},
): ApplyMoveResult {
  if (!state.holding) throw new Error('no holding tile');

  // 회전 검증
  const candidates = rotationCandidates(state.holding.number);
  if (!candidates.includes(resolvedNumber)) {
    throw new Error(`${state.holding.number} cannot rotate to ${resolvedNumber}`);
  }
  // 이미 결정된 경우 그 값만 허용
  if (state.holding.resolvedNumber !== null && state.holding.resolvedNumber !== resolvedNumber) {
    throw new Error(
      `holding tile already resolved to ${state.holding.resolvedNumber}, cannot use ${resolvedNumber}`,
    );
  }

  const player = state.players[state.currentPlayerIdx];
  if (!player) throw new Error('current player missing');

  if (!isPlaceable(player.lane, resolvedNumber, targetIdx)) {
    throw new Error(`cannot place ${resolvedNumber} at slot ${targetIdx}`);
  }

  // 1. 타겟 자리의 비공개 타일을 더미로 (비공개 그대로)
  const oldSlot = player.lane.slots[targetIdx];
  if (!oldSlot) throw new Error('unreachable');
  const replacedDownTile: Tile = {
    ...oldSlot.tile,
    face: 'DOWN',
    resolvedNumber: null,
  };

  // 2. holding 타일을 그 자리에 (face=UP, resolvedNumber 설정). source는 보드 타일에 불필요.
  const newUpTile: Tile = {
    id: state.holding.id,
    number: state.holding.number,
    face: 'UP',
    resolvedNumber,
  };

  // 3. 새 lane 구성
  const newSlots: LaneSlot[] = player.lane.slots.slice();
  newSlots[targetIdx] = { tile: newUpTile };
  const newLane: PlayerLane = { slots: newSlots };

  // 4. 더미에 비공개 타일 추가 (셔플 안 함; 어차피 비공개라 무관)
  const newDeck = [...state.center.deck, replacedDownTile];

  // 5. 플레이어 갱신
  const newPlayer: Player = { ...player, lane: newLane };
  const newPlayers = state.players.slice();
  newPlayers[state.currentPlayerIdx] = newPlayer;

  // 6. 보너스 / 승리 판정
  const bonus = isBonusTrigger(newLane, targetIdx, resolvedNumber);
  const isWinner = laneIsFullyRevealed(newLane);

  const newCenter: CenterArea = { deck: newDeck, openPool: state.center.openPool };

  let nextStatus = state.status;
  let nextWinnerId = state.winnerId;
  let nextCurrentIdx = state.currentPlayerIdx;
  let nextBonusActive = false;
  let nextMetrics = state.metrics;
  const now = options.now ?? Date.now();
  let nextTurnDeadline: number | null = state.turnDeadline;

  if (isWinner) {
    nextStatus = 'FINISHED';
    nextWinnerId = newPlayer.id;
    nextMetrics = { ...state.metrics, finishedAt: now };
    nextTurnDeadline = null;
  } else if (bonus) {
    nextBonusActive = true;
    // 같은 플레이어 계속 — 새 턴으로 간주해 deadline 갱신
    nextTurnDeadline = now + TURN_TIME_LIMIT_MS;
  } else {
    nextCurrentIdx = (state.currentPlayerIdx + 1) % state.players.length;
    nextTurnDeadline = now + TURN_TIME_LIMIT_MS;
  }

  return {
    state: {
      ...state,
      players: newPlayers,
      center: newCenter,
      holding: null,
      currentPlayerIdx: nextCurrentIdx,
      bonusActive: nextBonusActive,
      status: nextStatus,
      winnerId: nextWinnerId,
      metrics: nextMetrics,
      turnDeadline: nextTurnDeadline,
    },
    bonusEarned: bonus,
  };
}

function applyDiscardToPool(state: GameState, options: ApplyMoveOptions = {}): ApplyMoveResult {
  if (!state.holding) throw new Error('no holding tile');
  // RULES §4: DISCARD는 A-1(덱에서 뽑아 놓을 곳 없는 경우)에만 허용.
  // 풀에서 가져온 타일은 다시 풀로 보낼 수 없다 (반드시 PLACE).
  if (state.holding.source === 'POOL') {
    throw new Error('cannot discard pool-sourced tile back to pool');
  }

  // DISCARD는 회전 결정을 발생시키지 않는다 (RULES §3.2):
  // 풀로 버려진 타일은 다음에 가져가는 플레이어가 회전 후보 중 자유롭게 결정.
  const discardedTile: Tile = {
    id: state.holding.id,
    number: state.holding.number,
    face: 'UP',
    resolvedNumber: state.holding.resolvedNumber,
  };
  const newPool = [...state.center.openPool, discardedTile];

  const nextCurrentIdx = (state.currentPlayerIdx + 1) % state.players.length;
  const now = options.now ?? Date.now();

  return {
    state: {
      ...state,
      center: { deck: state.center.deck, openPool: newPool },
      holding: null,
      currentPlayerIdx: nextCurrentIdx,
      bonusActive: false,
      turnDeadline: now + TURN_TIME_LIMIT_MS,
    },
    bonusEarned: false,
  };
}

/**
 * 액션 B: 비공개 타일을 다른 자리에 끼워넣기 (RULES.md v2.3 §4).
 *
 * 동작: fromIdx의 비공개 타일을 빼서 toIdx 자리에 끼워넣는다.
 * 끼워넣는 자리부터 우측 모든 타일이 한 칸씩 뒤로 밀리며, 빠진 자리는
 * 그 좌측 타일들이 한 칸씩 우측으로 시프트해 메운다 (표준 splice 동작).
 * 공개 타일도 시프트되지만 *상대적 순서*는 유지되므로 좌→우 오름차순 제약은 자동 보존된다.
 *
 * 검증:
 * - holding 중이 아니어야 함
 * - fromIdx ≠ toIdx
 * - 두 인덱스 모두 [0, slots.length) 범위
 * - fromIdx의 타일은 face='DOWN'
 * - toIdx의 비공개 제약 없음 (공개 자리에도 끼워넣기 가능)
 */
function applyShuffleDownTile(
  state: GameState,
  fromIdx: number,
  toIdx: number,
  options: ApplyMoveOptions = {},
): ApplyMoveResult {
  if (state.holding) throw new Error('cannot shuffle while holding');
  if (fromIdx === toIdx) throw new Error('from and to must differ');

  const player = state.players[state.currentPlayerIdx];
  if (!player) throw new Error('current player missing');

  const slots = player.lane.slots;
  if (fromIdx < 0 || fromIdx >= slots.length) throw new Error('invalid fromIdx');
  if (toIdx < 0 || toIdx >= slots.length) throw new Error('invalid toIdx');

  const fromSlot = slots[fromIdx];
  if (!fromSlot) throw new Error('invalid fromIdx');
  if (fromSlot.tile.face !== 'DOWN') {
    throw new Error('only DOWN tile can be moved');
  }

  // splice 한 쌍으로 재배치 (22칸 길이 보존)
  const newSlots = slots.slice();
  const [moved] = newSlots.splice(fromIdx, 1);
  if (!moved) throw new Error('unreachable: splice returned empty');
  newSlots.splice(toIdx, 0, moved);

  const newPlayer: Player = { ...player, lane: { slots: newSlots } };
  const newPlayers = state.players.slice();
  newPlayers[state.currentPlayerIdx] = newPlayer;

  const nextCurrentIdx = (state.currentPlayerIdx + 1) % state.players.length;
  const now = options.now ?? Date.now();

  return {
    state: {
      ...state,
      players: newPlayers,
      currentPlayerIdx: nextCurrentIdx,
      bonusActive: false,
      turnDeadline: now + TURN_TIME_LIMIT_MS,
    },
    bonusEarned: false,
  };
}
